import type { InstallableModel, LocalAiRuntime, ModelDtype, VoiceManifest, VoiceModel, WorkerConfig } from '../types/LocalAiTypes';
import { needsGpu, resolveModel } from './ModelWeights';

export { needsGpu };

export function requireModel(manifest: VoiceManifest, key: VoiceModel['key']): VoiceModel {
  const model = manifest.models.models.find((item) => item.key === key && item.enabled);
  if (!model) throw new Error(`The server did not publish the ${key} local model.`);
  return model;
}

export function workerModelConfig(
  manifest: VoiceManifest,
  key: Exclude<VoiceModel['key'], 'runtime.onnx'>,
  runtime: LocalAiRuntime,
  halfPrecision: boolean,
): WorkerConfig {
  const model = resolveModel(requireModel(manifest, key), halfPrecision);
  const ort = requireModel(manifest, 'runtime.onnx');
  // ONNX Runtime's WebGPU bundle imports the Asyncify worker artifact. The
  // JSEP artifact does not export webgpuInit and fails during session setup.
  const stem = runtime === 'webgpu' ? 'ort-wasm-simd-threaded.asyncify' : 'ort-wasm-simd-threaded';
  const module = ort.assets.find((asset) => asset.path === `${stem}.mjs`);
  const binary = ort.assets.find((asset) => asset.path === `${stem}.wasm`);
  if (!module || !binary) throw new Error('The self-hosted ONNX runtime package is incomplete.');
  return {
    modelId: model.id,
    revision: model.revision,
    runtime,
    wasmModuleUrl: module.url,
    wasmBinaryUrl: binary.url,
    dtype: runnableDtype(model, runtime),
  };
}

/**
 * The weight format to ask the worker for, refused where this backend cannot run it.
 *
 * The format is a file name inside the export — `q4f16` means `encoder_model_q4f16.onnx` — so
 * it comes from the server, and `resolveModel` has already picked the one this *device* can
 * run. What is left is the backend: the fallback chain can point a checkpoint installed for
 * the GPU at the CPU, and half-precision weights have no path there.
 */
export function runnableDtype(model: InstallableModel, runtime: LocalAiRuntime): ModelDtype {
  if (model.dtype === '') throw new Error(`${model.displayName} does not publish a weight format.`);
  /*
   * Half-precision weights loaded on the WASM backend are either refused by the runtime
   * outright or emulated at a speed nobody would wait through, and either way the useful thing
   * to say is which model to install instead — see `speechAttempts`, which is what keeps this
   * from being reachable in the ordinary case.
   */
  if (runtime !== 'webgpu' && needsGpu(model.dtype)) {
    throw new Error(`${model.displayName} needs WebGPU, which this browser does not provide.`);
  }
  return model.dtype;
}
